<ng-container *ngIf="knowledge$ | async as state">
  <!-- loaded state -->
  <ng-container *ngIf="!state.loading && !state.error">
    <!-- Empty state -->
    <div
      [hidden]="state.sources && state.sources.length > 0"
      class="border rounded border-smoke p-12 flex flex-col items-center gap-6">
      <ng-container *ngTemplateOutlet="emptyStateSvg"></ng-container>

      <h3 class="text-lg text-center">Knowledge Hub for {{ state.property.name }}</h3>

      <div class="font-heading text-center text-gray-600 leading-normal">
        Generate your Knowledge Hub to get the most out of AI.
      </div>

      <div class="text-center text-gray-600 leading-normal">
        By compiling all your property's details, as well as all past conversations with guests, the AI will be able to
        respond to guests more effectively. You may further enhance the Knowledge Hub by updating generated topics, and
        importing additional information from a guidebook.
      </div>

      <!-- Uploading PDF -->
      <mat-progress-bar
        *ngIf="state.uploading"
        class="max-w-3xl"
        mode="determinate"
        [value]="
          state.uploadProgress.length > 0 ? state.uploadProgress[state.uploadProgress.length - 1].progress * 100 : 0
        "></mat-progress-bar>
      <div *ngIf="state.uploadProgress.length > 0" class="flex flex-col-reverse gap-2">
        <div
          *ngFor="let progress of state.uploadProgress; let last = last; trackBy: trackProgressFn"
          class="text-center text-smoke font-bold animate-fade-down animate-once animate-duration-200"
          [class.!text-grass-darker]="last"
          [class.animate-pulse]="last">
          {{ progress.message }}...
        </div>
      </div>

      <div class="flex items-center justify-center mt-4 space-x-4">
        <div [hidden]="state.uploading">
          <button
            class="bg-raspberry-darker p-4 rounded text-white cursor-pointer hover:brightness-95"
            (click)="onImportMessageHistoryClicked()">
            Generate Knowledge Hub
          </button>
        </div>
      </div>
      <div [hidden]="state.uploading" class="mt-8 text-sm">
        <a routerLink="/apps/touchstay" class="inline-flex gap-1 items-center">
          <span>Got a</span>
          <span>
            <img class="touchstay-logo h-4 w-4" src="/assets/images/apps/touchstay-logo.png" alt="TouchStay logo" />
            TouchStay
          </span>
          <span>account? Import your existing TouchStay guidebook automatically!</span>
        </a>
      </div>
    </div>

    <!-- Happy path with data -->
    <ng-container *ngIf="state.sources && state.sources.length > 0">
      <!-- Sample questions -->
      <section
        *ngIf="state.questions && state.questions.length > 0"
        class="border rounded border-smoke bg-white shadow p-4 mt-4 mb-8 top-8 z-10"
        [class.sticky]="!aiToggleOpen">
        <sbnb-collapsible-card [showDivider]="false" (openToggled)="onAiToggle($event)">
          <h4 class="text-grape-darker flex gap-2 items-center" title>
            <img
              class="question-icon"
              src="/assets/iconography/question.svg"
              onload="SVGInject(this)"
              width="16"
              height="16" />
            <span>Ask the AI</span>
          </h4>

          <ng-template sbnbCollapsibleCardContent>
            <div class="text-sm mt-1 text-night-lighter">
              Check how the AI will use the Knowledge Hub to respond to typical questions a guest might ask.
            </div>

            <section class="mt-3 divide-y divide-smoke">
              <div
                *ngFor="let question of state.questions"
                class="hover:bg-smoke-lighter cursor-pointer -mx-4 px-4 group"
                [class.bg-grass-lighter]="question.computed_reply"
                (click)="onTestResponse(question)">
                <div class="flex items-center gap-2 py-2 h-11">
                  <div class="flex-1" [class.strong]="question.computed_reply">
                    {{ question.query }}
                  </div>
                  <mat-spinner *ngIf="question.computed_loading" diameter="16"></mat-spinner>
                  <sbnb-button
                    *ngIf="!question.computed_loading"
                    class="invisible group-hover:visible"
                    size="mini"
                    type="secondary">
                    Ask question
                  </sbnb-button>
                </div>

                <div *ngIf="question.computed_reply" class="text-sm text-night pb-4">
                  {{ question.computed_reply }}
                </div>
              </div>
            </section>

            <!-- Custom question form -->
            <form [formGroup]="customQuestionForm" (ngSubmit)="onAskCustomQuestion()" class="mt-6">
              <div class="flex items-center gap-2">
                <mat-form-field class="flex-grow custom-question-form-field">
                  <input
                    matInput
                    formControlName="customQuestion"
                    placeholder="Ask your own question..."
                    (keyup.enter)="onAskCustomQuestion()" />
                </mat-form-field>
                <sbnb-button
                  size="mini"
                  class="custom-question-button"
                  [disabled]="!customQuestionForm.get('customQuestion')?.value?.trim()">
                  Ask
                </sbnb-button>
              </div>
            </form>

            <!-- Custom question result section -->
            <section *ngIf="customQuestion" class="mt-3 divide-y divide-smoke">
              <div
                class="hover:bg-smoke-lighter cursor-pointer -mx-4 px-4 group"
                [class.bg-grass-lighter]="customQuestion.computed_reply">
                <div class="flex items-center gap-2 py-2 h-11">
                  <div class="flex-1" [class.strong]="customQuestion.computed_reply">
                    {{ customQuestion.query }}
                  </div>
                  <mat-spinner *ngIf="customQuestion.computed_loading" diameter="16"></mat-spinner>
                </div>

                <div *ngIf="customQuestion.computed_reply" class="text-sm text-night pb-4">
                  {{ customQuestion.computed_reply }}
                </div>
              </div>
            </section>
          </ng-template>
        </sbnb-collapsible-card>
      </section>

      <!-- Sources -->
      <section class="border rounded border-smoke bg-white shadow p-4 mt-4 mb-8 top-8 z-10">
        <input
          #newFileUploadInput
          id="new-file"
          type="file"
          accept="application/pdf"
          class="hidden"
          (change)="onAddNewSourceClicked(newFileUploadInput)" />
        <input
          #fileReuploadInput
          id="reupload-file"
          type="file"
          accept="application/pdf"
          class="hidden"
          (change)="onReuploadSourceClicked(fileReuploadInput)" />

        <sbnb-collapsible-card [showDivider]="false" (openToggled)="onSourcesToggle($event)">
          <h4 class="text-grape-darker flex gap-2 items-center" title>
            <img
              class="information-icon"
              src="/assets/iconography/information.svg"
              onload="SVGInject(this)"
              width="16"
              height="16" />
            <span>Sources {{ state.sources.length > 0 ? '(' + state.sources.length + ')' : '' }}</span>
          </h4>

          <ng-template sbnbCollapsibleCardContent>
            <div class="text-sm mt-1 text-night-lighter">
              The following sources are used to compile and consolidate knowledge for AI.
            </div>

            <section class="mt-3 divide-y divide-smoke">
              <div class="flex items-center justify-between gap-2 py-2" *ngFor="let source of state.sources">
                <div class="flex flex-col text-sm text-grape-darker" *ngIf="source.state !== 'archived'">
                  <span class="inline-flex items-center" *ngIf="source.type === 'touch_stay'">
                    <img
                      class="touchstay-logo h-3 w-3"
                      src="/assets/images/apps/touchstay-logo.png"
                      alt="TouchStay logo" />
                    TouchStay
                  </span>
                  <span *ngIf="source.type !== 'touch_stay'">
                    {{ source.name }}
                  </span>
                  <span
                    class="flex items-center gap-1 text-night-lighter"
                    *ngIf="!['ready', 'error'].includes(source.state)">
                    <mat-spinner diameter="16"></mat-spinner>
                    <ng-container [ngSwitch]="source.state">
                      <ng-container *ngSwitchCase="'uploading'">Uploading...</ng-container>
                      <ng-container *ngSwitchCase="'created'">Processing...</ng-container>
                      <ng-container *ngSwitchCase="'extracting_text'">Extracting text...</ng-container>
                      <ng-container *ngSwitchCase="'structuring_text'">Structuring text...</ng-container>
                      <ng-container *ngSwitchCase="'aggregating_items'">Aggregating items...</ng-container>
                      <ng-container *ngSwitchCase="'finding_citations'">Finding citations...</ng-container>
                      <ng-container *ngSwitchCase="'finishing_up'">Finishing up...</ng-container>
                      <ng-container *ngSwitchCase="'deleting'">Deleting...</ng-container>
                    </ng-container>
                  </span>
                  <span class="text-night-lighter" *ngIf="source.state === 'ready'">
                    Last updated: {{ source.parsed_at | date: 'yyyy-MM-dd HH:mm' }}
                  </span>
                  <span class="text-night-lighter" *ngIf="source.state === 'error'">Error</span>
                </div>

                <div class="flex-shrink-0 w-10">
                  <ng-container
                    [ngTemplateOutlet]="sourceActionsTemplate"
                    [ngTemplateOutletContext]="{ source }"
                    *ngIf="['message_history', 'pdf'].includes(source.type)"></ng-container>
                </div>
              </div>
            </section>

            <section class="mt-3">
              <sbnb-button
                class="inline-block"
                size="mini"
                type="primary"
                (click)="triggerNewFileUploadInput()"
                [disabled]="sourcesProcessing$ | async"
                [matTooltip]="
                  (sourcesProcessing$ | async)
                    ? sourcesProcessingMessage
                    : 'Upload a PDF file to help the AI provide better responses to guests. Useful information includes: rules and policies, operating instructions, local area and restaurant recommendations, and safety and emergency notes.'
                ">
                Add new source
              </sbnb-button>
            </section>
          </ng-template>
        </sbnb-collapsible-card>
      </section>

      <!-- Header -->
      <section class="flex flex-col lg:flex-row justify-between gap-4 pb-1 border-b border-smoke">
        <div class="flex gap-2 justify-between">
          <h4 class="text-lg text-grape-darker strong">Knowledge for this property</h4>
        </div>

        <div class="flex items-start gap-2">
          <sbnb-search-bar
            class="flex-1 min-w-40 hidden xl:block"
            placeholderText="Search knowledge..."
            (searchChanged)="searchCriteria.next($event)"></sbnb-search-bar>

          <sbnb-button size="mini" type="secondary" (click)="triggerPosthogFeedback()">Give feedback</sbnb-button>

          <sbnb-button
            size="mini"
            type="primary"
            [disabled]="sourcesProcessing$ | async"
            [matTooltip]="(sourcesProcessing$ | async) ? sourcesProcessingMessage : ''"
            (click)="openNewItemDialog()">
            Add new item
          </sbnb-button>
        </div>
      </section>

      <!-- Topics + Aggregate Items -->
      <section *ngFor="let topic of filteredKnowledge$ | async">
        <div
          class="flex border-b border-smoke items-center py-4 gap-12 cursor-pointer hover:bg-smoke-lighter px-4"
          (click)="topic.computed_expanded = !topic.computed_expanded">
          <div class="flex items-center gap-2 flex-1">
            <div class="flex flex-col gap-2">
              <strong class="text-grape-darker">{{ topic.name }}</strong>
            </div>
            <mat-icon class="transition-all scale-105" [class.expand-open]="topic.computed_expanded">
              expand_more
            </mat-icon>
          </div>
        </div>

        <div *ngIf="topic.computed_expanded">
          <div
            *ngFor="let aggregateItem of topic.aggregate_items"
            class="flex text-sm py-3 gap-12 pl-8 items-center border-b border-smoke px-4"
            [class.opacity-30]="aggregateItem.deleting">
            <div class="flex-1 break-words whitespace-pre-line">
              {{ aggregateItem.content }}

              <div class="mt-0.5">
                <div class="flex items-center gap-1" *ngIf="!aggregateItem.deleting">
                  <span
                    class="text-sm text-night-lighter cursor-default"
                    [matTooltip]="aggregateItemSourceCount(aggregateItem)"
                    [matTooltipClass]="'text-xs'"
                    *ngIf="aggregateItem.sources.length > 0">
                    Sources ({{ aggregateItem.sources.length }})
                  </span>
                  <span
                    class="text-sm text-night-lighter cursor-default"
                    [matTooltip]="'Original:\n' + aggregateItem.original_content"
                    [matTooltipClass]="'text-xs'"
                    *ngIf="aggregateItem.sources.length > 0 && aggregateItem.is_edited">
                    - Edited
                  </span>
                  <span class="text-sm text-night-lighter cursor-default" *ngIf="aggregateItem.sources.length === 0">
                    Manual
                  </span>
                </div>

                <span class="flex items-center gap-1" *ngIf="aggregateItem.deleting">
                  <mat-spinner diameter="16"></mat-spinner>
                  Deleting...
                </span>
              </div>
            </div>

            <div class="text-xs hidden md:block">{{ aggregateItem.updated_at | date: 'longDate' }}</div>

            <div class="w-10">
              <ng-container
                [ngTemplateOutlet]="itemActionsTemplate"
                [ngTemplateOutletContext]="{ item: aggregateItem, topic }"></ng-container>
            </div>
          </div>
        </div>
      </section>
    </ng-container>
  </ng-container>

  <!-- loading state -->
  <sbnb-loading-spinner *ngIf="state.loading"></sbnb-loading-spinner>

  <!-- error state -->
  <div *ngIf="state.error" class="error">{{ state.error }}</div>

  <!-- debug -->
  <!-- <pre class="text-xs">{{ state | json }}</pre> -->
  <!-- <pre class="text-xs">{{ filteredKnowledge$ | async | json }}</pre> -->
</ng-container>

<ng-template #sourceActionsTemplate let-source="source">
  <sbnb-more-menu-trigger
    [matMenuTriggerFor]="actionsMenu"
    [suppressClick]="true"
    [disabled]="sourcesProcessing$ | async"
    [matTooltip]="(sourcesProcessing$ | async) ? sourcesProcessingMessage : ''"></sbnb-more-menu-trigger>
  <mat-menu #actionsMenu="matMenu">
    <button
      mat-menu-item
      *ngIf="source.type === 'pdf'"
      (click)="triggerReuploadFileInput(source)">
      <span>Reupload</span>
    </button>
    <button
      mat-menu-item
      *ngIf="['message_history', 'pdf'].includes(source.type)"
      (click)="onDeleteSourceClicked(source)">
      <span>Delete</span>
    </button>
  </mat-menu>
</ng-template>

<ng-template #itemActionsTemplate let-item="item" let-topic="topic">
  <sbnb-more-menu-trigger
    [matMenuTriggerFor]="actionsMenu"
    [suppressClick]="true"
    [disabled]="(sourcesProcessing$ | async) || item.deleting"
    [matTooltip]="(sourcesProcessing$ | async) ? sourcesProcessingMessage : ''"></sbnb-more-menu-trigger>
  <mat-menu #actionsMenu="matMenu">
    <button mat-menu-item (click)="openEditItemDialog(item, topic)">
      <span>Edit</span>
    </button>
    <button mat-menu-item (click)="onDeleteItemClicked(item)">
      <span>Delete</span>
    </button>
  </mat-menu>
</ng-template>

<ng-template #emptyStateSvg>
  <img src="/assets/images/knowledge-hub/empty-state-book-lightbulb.svg" alt="Empty state" />
</ng-template>
